import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, IconButton } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import {
  CustomContentProps,
  SnackbarContent,
  SnackbarKey,
  SnackbarMessage,
  SnackbarProvider,
  useSnackbar,
} from 'notistack';
import React, { forwardRef, ReactNode } from 'react';
import type { TitledNotificationSeverity } from './userNotification';

export const SuccessNotification = forwardRef<HTMLDivElement, CustomContentProps>(
  function SuccessNotification({ ...props }, ref) {
    return (
      <SnackbarContent ref={ref}>
        <Alert
          severity="success"
          elevation={6}
          variant="filled"
          sx={{
            color: 'white',
          }}
        >
          <b>Success: </b>
          {props.message}
        </Alert>
      </SnackbarContent>
    );
  },
) as unknown as {
  ({ ...props }: CustomContentProps & { ref?: React.Ref<HTMLDivElement> }): JSX.Element;
  displayName: string;
};
SuccessNotification.displayName = 'SuccessNotification';

export const ErrorNotification = forwardRef<HTMLDivElement, CustomContentProps>(
  function ErrorNotification({ ...props }, ref) {
    return (
      <SnackbarContent ref={ref}>
        <Alert
          severity="error"
          elevation={6}
          variant="filled"
          sx={{
            color: 'white',
          }}
        >
          <b>Error: </b>
          {props.message}
        </Alert>
      </SnackbarContent>
    );
  },
) as unknown as {
  ({ ...props }: CustomContentProps & { ref?: React.Ref<HTMLDivElement> }): JSX.Element;
  displayName: string;
};
ErrorNotification.displayName = 'ErrorNotification';

const titledNotificationIcons = {
  success: CheckCircleOutlineIcon,
  error: ErrorOutlineIcon,
  warning: WarningAmberIcon,
} as const;

type TitledNotificationCardProps = {
  severity: TitledNotificationSeverity;
  snackbarKey: SnackbarKey;
  children: SnackbarMessage;
};

/**
 * The look of a titled toast, Figma `Product` node 1878:584: filled, 310 px wide, the severity
 * icon, the message, then a close icon button. The fill and the icon colour come from the severity's
 * palette entry: success G500, error R500, warning Y500 with black text and icons
 * (`palette.warning.main`, never `status.warning.fill`, which the clarity light theme sets to
 * shade 600). Forced to the clarity theme like `XpmIntegrationWrapper`, so an app on another theme
 * still shows the Design System colours.
 */
const TitledNotificationCard = withClarityTheme(
  ({ severity, snackbarKey, children }: TitledNotificationCardProps) => {
    const { closeSnackbar } = useSnackbar();
    const Icon = titledNotificationIcons[severity];

    return (
      <Box
        // An error interrupts, success and warning inform: the roles the design boards use.
        role={severity === 'error' ? 'alert' : 'status'}
        sx={(theme) => ({
          // Hardcoded: Figma 310px wide, no matching size token
          width: 310,
          // Below the sm breakpoint notistack stretches its root across the screen; keep the toast
          // at the top right instead of the top left.
          marginLeft: 'auto',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'flex-start',
          gap: theme.semantic.spacing.sm,
          px: theme.semantic.spacing.base,
          py: theme.semantic.spacing.sm,
          borderRadius: theme.semantic.borderRadius.sm,
          backgroundColor: theme.palette[severity].main,
          color: theme.palette[severity].contrastText,
          boxShadow: theme.shadows[6],
        })}
      >
        <Icon sx={{ flexShrink: 0 }} />
        {children}
        <IconButton
          aria-label="Close"
          data-action="close-notification"
          color="inherit"
          onClick={() => closeSnackbar(snackbarKey)}
          sx={{ flexShrink: 0, p: 0 }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    );
  },
);

const createTitledNotification = (severity: TitledNotificationSeverity) => {
  const TitledNotification = forwardRef<HTMLDivElement, CustomContentProps>(
    function TitledNotification({ id, message }, ref) {
      return (
        <SnackbarContent ref={ref}>
          <TitledNotificationCard severity={severity} snackbarKey={id}>
            {message}
          </TitledNotificationCard>
        </SnackbarContent>
      );
    },
  );
  return TitledNotification;
};

const SuccessTitledNotification = createTitledNotification('success');
const ErrorTitledNotification = createTitledNotification('error');
const WarningTitledNotification = createTitledNotification('warning');

/**
 * notistack's `Components` map for the titled toasts that `enqueTitledNotification` shows. An app
 * that mounts its own `SnackbarProvider` spreads it beside its own entries:
 * `Components={{ successNotification: SuccessNotification, ...titledNotificationComponents }}`.
 */
export const titledNotificationComponents = {
  successTitledNotification: SuccessTitledNotification,
  errorTitledNotification: ErrorTitledNotification,
  warningTitledNotification: WarningTitledNotification,
};

/**
 * Mounts notistack's `SnackbarProvider`, wired to this package's own success/error content, so a
 * consumer can call `enqueSuccessNotification` / `enqueErrorNotification` without declaring a direct
 * dependency on `notistack` itself. `@verifime/components` is the package that owns that dependency;
 * everything else reaches the notification API through this export instead, the same way each app
 * mounts one `SnackbarProvider` in `src/pages/_app.tsx` rather than every consumer mounting its own.
 */
export const NotificationProvider = ({ children }: { children: ReactNode }) => (
  <SnackbarProvider
    Components={{
      successNotification: SuccessNotification,
      errorNotification: ErrorNotification,
      ...titledNotificationComponents,
    }}
  >
    {children}
  </SnackbarProvider>
);
